import styled, { css } from 'styled-components'
import { Row, Column, Button } from '@sainsburys-tech/bolt'
import { createCSSObject, remCalculator } from '../../../helpers/theming/utilities'
import { themeContract } from './M050.theme.contract.js'
import { RichText, dropShadowCSS } from '../../../helpers/StyledWrappers.styles'

const mediaRatioObject = {
  '16-by-9': '56.25%',
  '3-by-2': '66.66%',
  '1-by-1': '100%',
  '3-by-4': '133.33%'
}

const setPosition = (position) => css`
  margin-right: ${position === 'right' ? 0 : remCalculator(15)};
  margin-left: ${position === 'right' ? remCalculator(15) : 0};
  order: ${position === 'right' ? 2 : 1};
`
const textPadding = (value, theme) => (value ? remCalculator(theme.size.spacing[value]) : 0)

const Container = styled(Row)`
  ${dropShadowCSS}

  flex-basis: auto;
  overflow: hidden;
  margin-bottom: ${(props) => props.theme.size.spacing.lg};
  background: ${(props) => props.theme.color.monochrome.white};
  height: calc(100% - 1rem);
  align-content: flex-start;
`

const LinkList = styled.ul`
  margin: 0;
  list-style: none;

  ${(props) =>
    props.hasButton &&
    css`
      margin-bottom: ${props.theme.size.spacing.md};
    `}

  ${(props) =>
    !props.hasLink &&
    css`
      display: flex;
      flex-direction: row;
      justify-content: ${props.textAlign === 'center' ? 'center' : 'flex-start'};
      align-items: center;
    `}

  li {
    ${(props) => css`
      margin-bottom: ${props.theme.size.spacing.md};
      ${props.hasButton &&
      css`
        margin-top: ${props.theme.size.spacing.md};
      `}
      ${props.hasButtons &&
      css`
        &:not(:last-child) {
          margin-right: ${props.theme.size.spacing.md};
        }
      `}
    `}
  }
`

const Heading = styled.h3`
  && {
    ${(props) =>
      css`
        ${props.theme.channel !== 'sainsburys' &&
        createCSSObject(props.theme.font.stack[themeContract[props.theme.channel].heading.display])}
        color: ${props.theme.color.palette[themeContract[props.theme.channel].heading.color]};
        margin-bottom: ${themeContract[props.theme.channel].heading.marginBottom};

        ${props.textPosition === 'bottom' &&
        css`
          @media (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
            padding-top: ${props.theme.channel === 'sainsburys' ? 0 : props.theme.size.spacing.lg};
          }
        `}
      `}
  }
`

const MediaRatioWrapper = styled.div`
  position: relative;

  &::before {
    content: '';
    display: block;
    padding-bottom: ${(props) => mediaRatioObject[props.mediaRatio]};
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`

const MediaWrapper = styled(Column)`
  position: relative;
  margin-bottom: ${(props) => props.theme.size.spacing.lg};

  ${(props) =>
    props.hasFullWidthMedia
      ? css`
          margin-left: 0;
          @media (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
            margin-bottom: 0;
          }
        `
      : css`
          @media (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
            margin-right: ${props.imagePosition === 'right'
              ? 0
              : remCalculator(15)}; // Not using token as spacing above 30px will push the image below the text
            margin-left: ${props.imagePosition === 'right'
              ? remCalculator(15)
              : 0}; // Not using token as spacing above 30px will push the image below the text
            order: ${props.imagePosition === 'right' ? 2 : 1};
            margin-bottom: 0;
            ${setPosition(props.imagePosition)}
          }
        `}
`

const TextWrapper = styled(Column)`
  ${(props) =>
    css`
      text-align: ${props.textAlign};
      padding: ${props.hasPadding
        ? textPadding(themeContract[props.theme.channel].text.mobilePadding, props.theme)
        : 0};
      ${props.isSainsburys &&
      css`
        padding: 0 ${props.theme.size.spacing.lg} 0 ${props.theme.size.spacing.lg};
      `}

      @media (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
        padding: ${props.hasPadding
          ? textPadding(themeContract[props.theme.channel].text.desktopPadding, props.theme)
          : 0};
        display: flex;
        align-items: center;
        ${setPosition(props.textPosition)}
        ${props.golPadding &&
        css`
          margin-top: ${props.theme.size.spacing.md};
          padding-left: ${props.textPosition === 'right' ? 0 : props.theme.size.spacing.lg};
          margin-left: 0;
        `}
      }
    `}
`

const TextFlex = styled.div`
  width: 100%;

  a:first-of-type {
    margin-top: ${(props) => props.theme.size.spacing.lg};
  }

  ${(props) =>
    props.dropShadow &&
    css`
      padding: 0 ${props.theme.size.spacing.lg};
    `}
`

const RichTextWrapper = styled.div`
  ${RichText}
`

const Cta = styled(Button)`
  ${(props) =>
    props.theme.channel === 'argos' &&
    css`
      ${props.kind === 'primary' &&
      css`
        color: ${props.theme.color.monochrome.white};
        background-color: ${props.theme.color.monochrome.darker};
        &:hover {
          color: ${props.theme.color.monochrome.white};
          background-color: ${props.theme.color.monochrome.dark};
      `}
      ${props.kind === 'secondary' &&
      css`
        color: ${props.theme.color.monochrome.darker};
        border-color: ${props.theme.color.monochrome.darker};
        &:hover {
          color: ${props.theme.color.monochrome.darker};
          border-color: ${props.theme.color.monochrome.darker};
        }
      `}
    `}
`

export { Container, LinkList, Heading, MediaRatioWrapper, MediaWrapper, TextWrapper, TextFlex, RichTextWrapper, Cta }
